import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { api, fileUrl, imgUrl } from "@/lib/api";
import { Trash2, X, Upload } from "lucide-react";

export function GelatoAdmin() {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [override, setOverride] = useState({ size: "", product_uid: "" });
  const [pricing, setPricing] = useState(null); // {config, last_sync}
  const [preview, setPreview] = useState([]);
  const [syncingPrice, setSyncingPrice] = useState(false);
  const load = () => {
    api.get("/admin/gelato/products").then(r => setData(r.data));
    api.get("/admin/pricing/config").then(r => setPricing(r.data));
    api.get("/pricing/preview").then(r => setPreview(r.data.sizes || []));
  };
  useEffect(() => { load(); }, []);
  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data: r } = await api.post("/admin/gelato/refresh");
      if (r.ok) toast.success(`Mapped ${r.mapped.length} sizes + ${r.frame_mapped || 0} frames`);
      else toast.error(`Refresh failed: ${r.reason}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Refresh failed"); }
    finally { setRefreshing(false); }
  };
  const syncPrices = async () => {
    setSyncingPrice(true);
    try {
      const { data: r } = await api.post("/admin/pricing/sync-now");
      if (r.ok) toast.success(`Synced ${r.updated} products · ${r.failed} failed · ${r.artworks_updated} artworks repriced`);
      else toast.error(r.reason);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); }
    finally { setSyncingPrice(false); }
  };
  const savePricing = async () => {
    try {
      await api.patch("/admin/pricing/config", pricing.config);
      toast.success("Markup saved. Next sync will apply it.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const upsert = async () => {
    if (!override.size || !override.product_uid) { toast.error("Both size and product UID required"); return; }
    await api.patch("/admin/gelato/products", override);
    toast.success("Saved");
    setOverride({ size: "", product_uid: "" });
    load();
  };
  const del = async (size) => {
    if (!confirm(`Remove Gelato mapping for ${size}?`)) return;
    await api.delete(`/admin/gelato/products/${size}`);
    load();
  };
  if (!data || !pricing) return <div className="text-white/50">Loading…</div>;
  const cfg = pricing.config;
  const last = pricing.last_sync;
  return (
    <div>
      <h1 className="font-serif text-4xl mb-1">Prints — Gelato</h1>
      <div className="overline text-white/40 mb-8">Print-on-demand catalog, pricing & auto-fulfillment</div>

      <div className="surface p-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="overline text-white/50">Integration Status</div>
            <div className="text-lg mt-1">
              {data.configured ? <span className="text-emerald-300">● Connected</span> : <span className="text-red-400">● Not configured</span>}
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={refresh} disabled={refreshing || !data.configured} data-testid="gelato-refresh"
              className="bg-white/10 text-white px-4 py-2 text-xs uppercase tracking-widest hover:bg-white/20 disabled:opacity-50">
              {refreshing ? "Refreshing…" : "Refresh Catalog"}
            </button>
            <button onClick={syncPrices} disabled={syncingPrice || !data.configured} data-testid="pricing-sync-now"
              className="bg-white text-black px-4 py-2 text-xs uppercase tracking-widest disabled:opacity-50">
              {syncingPrice ? "Syncing…" : "Sync Prices Now"}
            </button>
          </div>
        </div>
        <div className="text-xs text-white/50 mt-3 font-mono-brand">
          Webhook URL: <span className="text-white/80">{window.location.origin}/api/webhook/gelato</span> · header <span className="text-white/80">X-Gelato-Webhook-Secret</span>
        </div>
        {last && (
          <div className="text-xs text-white/50 mt-2 font-mono-brand">
            Last price sync: {last.at?.slice(0,16).replace("T"," ")} · updated {last.updated} · failed {last.failed} · repriced {last.artworks_updated} artworks
          </div>
        )}
      </div>

      {/* Markup formula */}
      <div className="surface p-5 mb-6">
        <div className="overline text-white/50 mb-4">Markup Formula (retail = landed cost × multiplier)</div>
        <div className="grid md:grid-cols-4 gap-3">
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-white/40">Default</span>
            <input type="number" step="0.01" data-testid="markup-default" className="mt-1 w-full bg-black/40 border border-white/10 px-3 py-2 text-sm"
              value={cfg.default_multiplier} onChange={e => setPricing({ ...pricing, config: { ...cfg, default_multiplier: Number(e.target.value) } })}/>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-white/40">Premium (framed / fine-art)</span>
            <input type="number" step="0.01" data-testid="markup-premium" className="mt-1 w-full bg-black/40 border border-white/10 px-3 py-2 text-sm"
              value={cfg.premium_multiplier} onChange={e => setPricing({ ...pricing, config: { ...cfg, premium_multiplier: Number(e.target.value) } })}/>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-white/40">Bulk 10–24</span>
            <input type="number" step="0.01" data-testid="markup-bulk-10" className="mt-1 w-full bg-black/40 border border-white/10 px-3 py-2 text-sm"
              value={cfg.bulk_10_multiplier} onChange={e => setPricing({ ...pricing, config: { ...cfg, bulk_10_multiplier: Number(e.target.value) } })}/>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-white/40">Bulk 25+</span>
            <input type="number" step="0.01" data-testid="markup-bulk-25" className="mt-1 w-full bg-black/40 border border-white/10 px-3 py-2 text-sm"
              value={cfg.bulk_25_multiplier} onChange={e => setPricing({ ...pricing, config: { ...cfg, bulk_25_multiplier: Number(e.target.value) } })}/>
          </label>
        </div>
        <div className="mt-3 flex items-center gap-3">
          <button onClick={savePricing} data-testid="markup-save" className="bg-white text-black px-4 py-2 text-xs uppercase tracking-widest">Save Multipliers</button>
          <span className="text-xs text-white/40">Daily auto-sync runs at 03:00 UTC. Prices carry over on API failure.</span>
        </div>
      </div>

      {/* Retail preview */}
      <div className="surface p-5 mb-6">
        <div className="overline text-white/50 mb-3">Retail Prices (from last sync)</div>
        <table className="w-full text-sm font-mono-brand">
          <thead className="text-white/50 text-xs uppercase">
            <tr>
              <th className="text-left p-2">Size</th>
              <th className="text-right p-2">Landed Cost</th>
              <th className="text-right p-2">Base Retail</th>
              <th className="text-right p-2">Bulk 10–24</th>
              <th className="text-right p-2">Bulk 25+</th>
              <th className="text-left p-2">Last Sync</th>
            </tr>
          </thead>
          <tbody>
            {preview.map(p => (
              <tr key={p.size} className="border-t border-white/5" data-testid={`retail-${p.size}`}>
                <td className="p-2 font-bold">{p.size}</td>
                <td className="p-2 text-right text-white/70">{p.landed_cost_usd ? `$${p.landed_cost_usd.toFixed(2)}` : "—"}</td>
                <td className="p-2 text-right">{p.retail_price_usd ? `$${p.retail_price_usd.toFixed(2)}` : "—"}</td>
                <td className="p-2 text-right text-white/70">{p.retail_10bulk_usd ? `$${p.retail_10bulk_usd.toFixed(2)}` : "—"}</td>
                <td className="p-2 text-right text-white/70">{p.retail_25bulk_usd ? `$${p.retail_25bulk_usd.toFixed(2)}` : "—"}</td>
                <td className="p-2 text-white/40 text-[10px]">{p.last_synced?.slice(0,16).replace("T"," ") || "never"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="surface p-5 mb-6">
        <div className="overline text-white/50 mb-3">Print Size → Product Mapping</div>
        {data.mappings.length === 0 ? (
          <p className="text-white/50 text-sm">No mappings yet. Click "Refresh Catalog" to fetch them from Gelato.</p>
        ) : (
          <table className="w-full text-sm font-mono-brand">
            <thead className="text-white/50 text-xs uppercase">
              <tr>
                <th className="text-left p-2">Size</th>
                <th className="text-left p-2">Paper</th>
                <th className="text-left p-2">Product UID</th>
                <th className="text-right p-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {data.mappings.map(m => (
                <tr key={m.size} className="border-t border-white/5" data-testid={`gelato-map-${m.size}`}>
                  <td className="p-2 font-bold">{m.size}</td>
                  <td className="p-2 text-white/70">{m.paper || m.name}</td>
                  <td className="p-2 text-[10px] text-white/50 max-w-md truncate" title={m.product_uid}>{m.product_uid}</td>
                  <td className="p-2 text-right">
                    <button onClick={() => del(m.size)} className="text-red-400 text-xs"><Trash2 size={12}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <details className="surface p-5">
        <summary className="cursor-pointer overline text-white/60">Manual Override</summary>
        <div className="mt-4">
          <div className="text-xs text-white/50 mb-3">Add or replace a specific size mapping.</div>
          <div className="grid md:grid-cols-2 gap-3">
            <input placeholder="Size (e.g. 8x10)" className="bg-black/40 border border-white/10 px-3 py-2 text-sm" value={override.size} onChange={e => setOverride({ ...override, size: e.target.value })}/>
            <input placeholder="Gelato productUid" className="bg-black/40 border border-white/10 px-3 py-2 text-sm font-mono-brand" value={override.product_uid} onChange={e => setOverride({ ...override, product_uid: e.target.value })}/>
          </div>
          <button onClick={upsert} className="mt-3 bg-white text-black px-4 py-2 text-xs uppercase tracking-widest">Save Mapping</button>
        </div>
      </details>
    </div>
  );
}

export function BulkPublishFlow({ onDone }) {
  const CATS = ["Original Paintings", "Prints", "Custom Commissions", "Scenic", "Animals", "Flowers", "Abstract", "Charcoal Artwork"];
  const [open, setOpen] = useState(false);
  const [uploads, setUploads] = useState([]); // {name, ok, path, error, category, title, prints_only}
  const [batchCat, setBatchCat] = useState("Scenic");
  const [uploading, setUploading] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    // Client-side guard: separate images from non-images/oversized before POST
    const MAX = 15 * 1024 * 1024;
    const good = [];
    const rejected = [];
    for (const f of Array.from(files)) {
      if (!f.type.startsWith("image/")) {
        rejected.push({ name: f.name, ok: false, error: `Unsupported type: ${f.type || "unknown"}`, category: batchCat });
      } else if (f.size > MAX) {
        rejected.push({ name: f.name, ok: false, error: `Too large (${(f.size/1024/1024).toFixed(1)} MB > 15 MB)`, category: batchCat });
      } else {
        good.push(f);
      }
    }
    if (rejected.length > 0) setUploads(prev => [...prev, ...rejected]);
    if (good.length === 0) { if (rejected.length) toast.error(`${rejected.length} file(s) rejected`); return; }
    setUploading(true);
    const form = new FormData();
    good.forEach(f => form.append("files", f));
    try {
      const { data } = await api.post("/admin/upload-bulk", form);
      const next = data.results.map((r) => ({
        name: r.filename,
        ok: r.ok,
        path: r.path,
        error: r.error || "",
        title: r.filename ? r.filename.replace(/\.[^.]+$/, "").replace(/[-_]+/g, " ") : "",
        category: batchCat,
        prints_only: true,
      }));
      setUploads(prev => [...prev, ...next]);
      const ok = next.filter(n => n.ok).length;
      const fail = next.length - ok + rejected.length;
      if (ok) toast.success(`Uploaded ${ok} file${ok > 1 ? "s" : ""}`);
      if (fail) toast.error(`${fail} failed`);
    } catch (e) {
      console.error("Bulk upload error", e);
      toast.error("Bulk upload error");
    } finally { setUploading(false); }
  };

  const applyBatchCat = () => {
    setUploads(u => u.map(x => x.ok ? { ...x, category: batchCat } : x));
    toast.success(`All set to ${batchCat}`);
  };

  const publish = async () => {
    const items = uploads.filter(u => u.ok).map(u => ({
      storage_path: u.path, title: u.title, category: u.category, prints_only: u.prints_only,
    }));
    if (items.length === 0) { toast.error("Nothing to publish"); return; }
    setPublishing(true);
    try {
      const { data } = await api.post("/admin/bulk/publish", { items });
      toast.success(`Published ${data.count} artwork${data.count > 1 ? "s" : ""}`);
      setOpen(false);
      setUploads([]);
      onDone && onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Publish failed");
    } finally { setPublishing(false); }
  };

  const okUploads = useMemo(() => uploads.filter(u => u.ok), [uploads]);
  const failed = useMemo(() => uploads.filter(u => !u.ok), [uploads]);
  const okCount = okUploads.length;
  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid="bulk-upload-open"
        className="bg-white/10 text-white px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2 hover:bg-white/20">
        <Upload size={14}/> Bulk Upload
      </button>
    );
  }
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-6 overflow-y-auto" onClick={() => !uploading && !publishing && setOpen(false)}>
      <div className="surface p-6 max-w-6xl w-full my-8" onClick={e => e.stopPropagation()} data-testid="bulk-upload-modal">
        <div className="flex justify-between items-center mb-4">
          <div>
            <div className="font-serif text-2xl">Bulk Upload & Publish</div>
            <div className="overline text-white/40 mt-1">Review each artwork's category before it goes live</div>
          </div>
          <button onClick={() => setOpen(false)} className="text-white/50 hover:text-white"><X size={18}/></button>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-5">
          <label className="inline-flex items-center gap-2 text-xs cursor-pointer bg-white text-black px-4 py-2 uppercase tracking-widest">
            <Upload size={14}/> {uploading ? "Uploading…" : "Add Files"}
            <input type="file" accept="image/*" multiple className="hidden" data-testid="bulk-file-input"
              onChange={e => { upload(e.target.files); e.target.value = ""; }}/>
          </label>
          <select value={batchCat} onChange={e => setBatchCat(e.target.value)} data-testid="bulk-batch-category"
            className="bg-black/40 border border-white/10 px-3 py-2 text-sm">
            {CATS.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <button onClick={applyBatchCat} disabled={okCount === 0} data-testid="bulk-apply-batch-cat"
            className="bg-white/10 text-white px-3 py-2 text-xs uppercase tracking-widest disabled:opacity-40">
            Apply to All
          </button>
          <div className="text-xs text-white/50 ml-auto">Default set on new uploads · you can also change per image below</div>
        </div>

        {failed.length > 0 && (
          <div className="surface p-4 mb-5 border border-red-900" data-testid="bulk-failed-list">
            <div className="overline text-red-300 mb-2">{failed.length} Failed</div>
            <ul className="text-xs text-red-200 space-y-1">
              {failed.map((f) => <li key={f.name}>· <span className="font-mono-brand">{f.name}</span> — {f.error}</li>)}
            </ul>
          </div>
        )}

        {okCount === 0 ? (
          <div className="text-center text-white/40 py-16">Choose image files to begin.</div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {okUploads.map((u, i) => (
                <div key={u.path} className="surface p-3" data-testid={`bulk-tile-${i}`}>
                  <div className="aspect-square bg-black/40 overflow-hidden mb-3">
                    <img src={imgUrl(fileUrl(u.path), 320)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover"/>
                  </div>
                  <input value={u.title} onChange={e => setUploads(prev => prev.map(x => x.path === u.path ? { ...x, title: e.target.value } : x))}
                    className="w-full bg-black/40 border border-white/10 px-2 py-1 text-xs mb-2" placeholder="Title"/>
                  <select value={u.category} onChange={e => setUploads(prev => prev.map(x => x.path === u.path ? { ...x, category: e.target.value } : x))}
                    data-testid={`bulk-tile-cat-${i}`}
                    className="w-full bg-black/40 border border-white/10 px-2 py-1 text-xs">
                    {CATS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <label className="flex items-center gap-2 text-[10px] text-white/60 mt-2">
                    <input type="checkbox" checked={u.prints_only} onChange={e => setUploads(prev => prev.map(x => x.path === u.path ? { ...x, prints_only: e.target.checked } : x))}/>
                    Prints only (auto-priced)
                  </label>
                  <button onClick={() => setUploads(prev => prev.filter(x => x.path !== u.path))}
                    className="mt-2 text-[10px] text-red-400 hover:text-red-300">Remove</button>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between border-t border-white/10 pt-4">
              <div className="text-xs text-white/50">
                {okCount} ready · Publishing creates artworks with their assigned category and default print sizes.
              </div>
              <div className="flex gap-3">
                <button onClick={() => { setUploads([]); }} className="border border-white/20 px-4 py-2 text-xs uppercase tracking-widest text-white/70">Clear All</button>
                <button onClick={publish} disabled={publishing || okCount === 0} data-testid="bulk-publish-btn"
                  className="bg-white text-black px-6 py-2 text-xs uppercase tracking-widest disabled:opacity-40">
                  {publishing ? "Publishing…" : `Publish ${okCount} Artwork${okCount > 1 ? "s" : ""}`}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// end of file

